/**
 * The head of a page: what this is, what it is for, what you can do to it, and
 * the tabs that divide it.
 *
 * The title is Playfair, and it is the largest display type in the console.
 * `tokens.css` names the trade the whole design system is built around, and
 * this is where its answer lands: spend the warmth on the chrome, the page
 * titles and the empty states, and run the data regions on hairline rules and
 * a disciplined neutral grid. The serif here is what buys the table below it
 * the right to be as plain as it is.
 */

import { useId } from 'react'
import type { ReactNode } from 'react'
import styles from './PageHeader.module.css'

export interface PageHeaderProps {
  title: string
  /**
   * One or two sentences under the title.
   *
   * Kept, and no longer passed by the ordinary screens. Every page used to
   * carry a sentence explaining itself to somebody who was already looking at
   * it, and those are gone. What survives is the case where the sentence is
   * the content rather than narration: an empty state, where stripping the
   * prose leaves a blank rectangle, and the few screens where an organization
   * is being asked to decide what an AI may say to its community and cannot be
   * expected to arrive knowing what that means.
   *
   * Deleting the prop was considered and rejected. It would have been a
   * hundred call-site changes to remove sentences that were already being
   * removed, and it would have left those genuinely explanatory places with
   * nowhere to put the one line they need except a stray paragraph above the
   * content, which is the same text with none of the styling.
   */
  description?: ReactNode
  /**
   * A short uppercase tracked label above the title, in the accent: "Today",
   * "Broadcast", "Project workspace".
   *
   * This is what replaced the sentence. A page needs an identity more than it
   * needs a paragraph, and three words in the accent give it one in the space
   * a line of prose used to occupy, at a size that is read in a glance rather
   * than in a sentence.
   */
  eyebrow?: ReactNode
  /** Buttons. The primary action last, where the eye finishes. */
  actions?: ReactNode
  /** A tab list, rendered flush with the bottom rule. */
  tabs?: ReactNode
  /** Numbers, filters or a summary strip between the description and the tabs. */
  children?: ReactNode
  className?: string
  /**
   * An extra class on the `h1` alone, for a title that is not prose.
   *
   * The case screen puts a reference like `NR-2026-0184` here, and a
   * proportional face draws a capital I and a digit 1 the same, at the moment
   * two caseworkers are reading the string aloud to each other. Without this
   * the only way to reach the heading was a descendant selector from the
   * header's own class, which is a screen stylesheet reaching into a component's
   * markup and breaks silently the day this element changes.
   *
   * Deliberately a class and not a `titleAs` or a size prop: what varies is the
   * register, and the size, weight and leading must stay the page title's so
   * this cannot drift into a second heading style.
   */
  titleClassName?: string
}

export function PageHeader({
  title,
  description,
  eyebrow,
  actions,
  tabs,
  children,
  className,
  titleClassName,
}: PageHeaderProps) {
  const titleId = useId()
  const headerClassName = [styles.header, className].filter(Boolean).join(' ')

  return (
    /*
     * Labelled by its own title, so the page's main region is announced by name
     * rather than as "region". With several landmarks on screen at once, a
     * screen reader's landmark list is otherwise four entries all called the
     * same thing.
     */
    <header className={headerClassName} aria-labelledby={titleId}>
      <div className={styles.top}>
        <div className={styles.headings}>
          {eyebrow ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
          <h1 className={[styles.title, titleClassName].filter(Boolean).join(' ')} id={titleId}>
            {title}
          </h1>
          {description ? <p className={styles.description}>{description}</p> : null}
        </div>

        {actions ? <div className={styles.actions}>{actions}</div> : null}
      </div>

      {children ? <div className={styles.extra}>{children}</div> : null}

      {tabs ? <div className={styles.tabs}>{tabs}</div> : null}
    </header>
  )
}
